'use client';

import { useCallback, useEffect, useState } from 'react';

import { useProductDetails } from '@app/pdp/hooks/useProductDetails';
import { type MappedProductVariant } from '@libs/algolia';
import {
    AccountDrawerType,
    type FavoriteItem,
    useFavorites,
    usePageLayout,
} from '@libs/common-next';
import debounce from 'lodash.debounce';
import { CommonFavoriteProductIcon } from './CommonFavoriteProductIcon';

interface VelocityFavoriteProductIconProps {
    callAnalytics: (productId: MappedProductVariant) => void;
}

export default function VelocityFavoriteProductIcon({
    callAnalytics,
}: VelocityFavoriteProductIconProps) {
    const { selectedProduct } = useProductDetails();
    const { openAccountDrawer, user, setSignInDrawerProps } = usePageLayout();
    const { favorites, addToFavorites, removeFromFavorites } = useFavorites();

    const [isFavorite, setIsFavorite] = useState<boolean>(false);

    useEffect(() => {
        const favoriteItem = favorites?.find(
            (item: FavoriteItem) => item.sku === selectedProduct.id.toString()
        );
        setIsFavorite(!!favoriteItem);
    }, [selectedProduct.id, favorites]);

    const [addToFavAfterSignIn, setAddToFavAfterSignIn] =
        useState<boolean>(false);

    const callAddToFavorite = useCallback(
        async (favoriteProduct: MappedProductVariant) => {
            addToFavorites(favoriteProduct.id.toString());
            callAnalytics(favoriteProduct);
        },
        [addToFavorites, callAnalytics]
    );

    const callRemoveFromFavorite = useCallback(
        async (favoriteProduct: MappedProductVariant) => {
            removeFromFavorites(favoriteProduct.id.toString());
        },
        [removeFromFavorites]
    );

    const debouncedCallToggleFavorite = useCallback(
        debounce(
            (isProductFavorite: boolean) =>
                isProductFavorite
                    ? callAddToFavorite(selectedProduct)
                    : callRemoveFromFavorite(selectedProduct),
            1000
        ),
        [selectedProduct.id, callAddToFavorite, callRemoveFromFavorite]
    );

    const handleToggleFavorite = useCallback(() => {
        if (!user) {
            setAddToFavAfterSignIn(true);
            setSignInDrawerProps({
                productToAddToWishlist: selectedProduct.id.toString(),
            });
            openAccountDrawer(AccountDrawerType.SIGN_IN);
        } else {
            setIsFavorite(prev => !prev);
            debouncedCallToggleFavorite(!isFavorite);
        }
    }, [user, debouncedCallToggleFavorite, isFavorite]);

    useEffect(() => {
        if (addToFavAfterSignIn && favorites) {
            handleToggleFavorite();
            setAddToFavAfterSignIn(false);
        }
    }, [user, favorites]);

    return (
        <CommonFavoriteProductIcon
            isFavorite={isFavorite}
            handleToggleFavorite={handleToggleFavorite}
            callAddToFavorite={callAddToFavorite}
            setIsFavorite={setIsFavorite}
        />
    );
}
